import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { initializeUserRole, getUserRole, hasPermission, USER_ROLES } from '../services/userService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Initialize or get user role
          const roleData = await initializeUserRole(user.uid, user.email);
          setCurrentUser(user);
          setUserRole(roleData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Error loading user role:', error);
          // Deny access when role cannot be determined
          setCurrentUser(null);
          setUserRole(null);
          setIsAuthenticated(false);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    setAuthError('');
    try {
      // Regular login attempt
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get user role after login
      const roleData = await getUserRole(user.uid);
      
      // Check if user is active
      if (roleData.isActive === false) {
        await signOut(auth);
        throw new Error('Your account has been deactivated. Please contact administrator.');
      }
      
      setUserRole(roleData);
      return true;
    } catch (error) {
      console.error("Login error:", error.message);
      setAuthError(error.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserRole(null);
      setIsAuthenticated(false);
      return true;
    } catch (error) {
      console.error("Logout error:", error.message);
      return false;
    }
  };

  const resetPassword = async (email) => {
    setAuthError('');
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Password reset email sent successfully! Please check your inbox.' };
    } catch (error) {
      console.error("Password reset error:", error.message);
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please wait a moment and try again.';
          break;
        default:
          errorMessage = error.message;
      }
      
      setAuthError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Admin-only function to reset another user's password
  const adminResetUserPassword = async (email) => {
    // Check if current user is admin
    if (!userRole || userRole.role !== USER_ROLES.ADMIN) {
      return { success: false, message: 'Unauthorized: Admin access required.' };
    }

    try {
      await sendPasswordResetEmail(auth, email);
      return { 
        success: true, 
        message: `Password reset email sent to ${email}. The user will receive instructions to reset their password.` 
      };
    } catch (error) {
      console.error("Admin password reset error:", error.message);
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = `No account found with email: ${email}`;
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address provided.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please wait a moment and try again.';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, message: errorMessage };
    }
  };

  // Helper function to check permissions
  const checkPermission = (module, action) => {
    console.log('Checking permission:', { module, action, userRole, result: hasPermission(userRole?.role, module, action) });
    if (!userRole) return false;
    return hasPermission(userRole.role, module, action);
  };

  // Helper function to check if user is admin
  const isAdmin = () => {
    return userRole?.role === USER_ROLES.ADMIN;
  };

  // Helper function to check if user is staff
  const isStaff = () => {
    return userRole?.role === USER_ROLES.STAFF;
  };

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      userRole,
      isAuthenticated, 
      isLoading, 
      login, 
      logout, 
      resetPassword,
      adminResetUserPassword,
      authError,
      checkPermission,
      isAdmin,
      isStaff
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);